import { authAPI } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LogOut, Settings, GraduationCap, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { isStudentPreview, startStudentPreview, stopStudentPreview } from "@/lib/studentPreview";
import { SeletorDeSistemas } from "./SeletorDeSistemas";

interface SidebarProps {
  userType: "admin" | "aluno";
}

export const Sidebar = ({ userType }: SidebarProps) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(authAPI.getCurrentUser());
  const previewMode = isStudentPreview();

  // Recarregar dados do usuário ao montar o componente
  useEffect(() => {
    const refreshUser = async () => {
      const updatedUser = await authAPI.refreshCurrentUser();
      if (updatedUser) {
        setUser(updatedUser);
      }
    };
    refreshUser();

    // Listener para atualização de perfil
    const handleUserUpdate = () => {
      setUser(authAPI.getCurrentUser());
    };

    window.addEventListener('userUpdated', handleUserUpdate);

    return () => {
      window.removeEventListener('userUpdated', handleUserUpdate);
    };
  }, []);

  const handleLogout = () => {
    stopStudentPreview();
    authAPI.logout();
    toast.success("Logout realizado com sucesso!");
    navigate("/");
  };

  const handleStartPreview = () => {
    startStudentPreview();
    toast.info("Demonstração do acesso do aluno iniciada.");
    navigate("/aluno");
  };

  const handleExitPreview = () => {
    stopStudentPreview();
    toast.success("Retornando ao painel administrativo.");
    navigate("/admin");
  };

  const getInitials = (name?: string) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="w-64 bg-[#013079] text-white min-h-screen p-6 flex flex-col">
      <div className="mb-8">
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-2xl font-bold">Memoire</h1>
          {/* Atalho para os outros sistemas; no celular ele fica no topo (MainLayout). */}
          <SeletorDeSistemas className="hidden md:inline-flex" />
        </div>
        <p className="text-sm text-gray-300 mt-1">
          {userType === "admin" ? "Painel Admin" : "Painel do Aluno"}
        </p>
      </div>

      <div className="flex-1">
        <nav className="space-y-2">
          {userType === "admin" ? (
            <>
              <Button
                variant="ghost"
                className="w-full justify-start text-white hover:bg-white/10"
                onClick={() => navigate("/admin")}
              >
                Dashboard
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start text-white hover:bg-white/10"
                onClick={() => navigate("/admin/usuarios")}
              >
                Usuários
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start text-white hover:bg-white/10"
                onClick={() => navigate("/admin/cards")}
              >
                Cards
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start text-white hover:bg-white/10"
                onClick={() => navigate("/admin/categorias")}
              >
                Categorias
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start text-white hover:bg-white/10"
                onClick={() => navigate("/admin/grupos")}
              >
                Grupos
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start text-white hover:bg-white/10"
                onClick={handleStartPreview}
              >
                <GraduationCap className="w-4 h-4 mr-2" />
                Ver como aluno
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                className="w-full justify-start text-white hover:bg-white/10"
                onClick={() => navigate("/aluno")}
              >
                Dashboard
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start text-white hover:bg-white/10"
                onClick={() => navigate("/aluno/cards")}
              >
                Estudar
              </Button>

            </>
          )}
        </nav>
      </div>

      <div className="border-t border-white/20 pt-4 space-y-2">
        {userType === "aluno" && previewMode && (
          <Button
            variant="ghost"
            className="w-full justify-start text-white hover:bg-white/10"
            onClick={handleExitPreview}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao painel admin
          </Button>
        )}
        <div className="flex items-center gap-3 px-3 py-2">
          <Avatar className="h-10 w-10">
            <AvatarImage
              src={user?.foto ? (user.foto.startsWith('http') ? user.foto : `${import.meta.env.VITE_API_URL || '/api'}${user.foto}`) : undefined}
              alt={user?.nome}
              onError={(e) => {
                console.error('Error loading avatar:', user?.foto);
                console.log('User data:', user);
              }}
            />
            <AvatarFallback className="bg-white/20 text-white">
              {getInitials(user?.nome)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.nome}</p>
            <p className="text-xs text-gray-300 truncate">{user?.email}</p>
          </div>
        </div>

        <Button
          variant="ghost"
          className="w-full justify-start text-white hover:bg-white/10"
          onClick={() => navigate("/perfil")}
        >
          <Settings className="w-4 h-4 mr-2" />
          Configurações
        </Button>

        <Button
          variant="ghost"
          className="w-full justify-start text-white hover:bg-white/10"
          onClick={handleLogout}
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sair
        </Button>
      </div >
    </div >
  );
};
